/**
 * Web Bluetooth specific error codes
 */
export const WebBLEErrorCode = {
  /** Browser doesn't support Web Bluetooth */
  NOT_SUPPORTED: "NOT_SUPPORTED",

  /** Not in secure context (HTTPS required) */
  INSECURE_CONTEXT: "INSECURE_CONTEXT",

  /** User cancelled device selection */
  USER_CANCELLED: "USER_CANCELLED",

  /** Device not found or not available */
  DEVICE_NOT_FOUND: "DEVICE_NOT_FOUND",

  /** Failed to connect to GATT server */
  GATT_CONNECTION_FAILED: "GATT_CONNECTION_FAILED",

  /** Service not found on device */
  SERVICE_NOT_FOUND: "SERVICE_NOT_FOUND",

  /** Characteristic not found in service */
  CHARACTERISTIC_NOT_FOUND: "CHARACTERISTIC_NOT_FOUND",

  /** Failed to start notifications */
  NOTIFICATION_FAILED: "NOTIFICATION_FAILED",

  /** BLE write operation failed */
  WRITE_FAILED: "WRITE_FAILED",

  /** Device disconnected unexpectedly */
  UNEXPECTED_DISCONNECT: "UNEXPECTED_DISCONNECT",

  /** Permission denied */
  PERMISSION_DENIED: "PERMISSION_DENIED",

  /** Operation timed out */
  TIMEOUT: "TIMEOUT",
} as const;

export type WebBLEErrorCode =
  (typeof WebBLEErrorCode)[keyof typeof WebBLEErrorCode];

/**
 * Mapping of Web Bluetooth DOMException names to WebBLE error codes
 */
export const WEB_BLUETOOTH_ERROR_MAP: Record<string, WebBLEErrorCode> = {
  NotFoundError: WebBLEErrorCode.USER_CANCELLED,
  SecurityError: WebBLEErrorCode.INSECURE_CONTEXT,
  NotSupportedError: WebBLEErrorCode.NOT_SUPPORTED,
  NetworkError: WebBLEErrorCode.UNEXPECTED_DISCONNECT,
  TimeoutError: WebBLEErrorCode.TIMEOUT,
  NotAllowedError: WebBLEErrorCode.PERMISSION_DENIED,
};

/**
 * User-friendly error messages with solutions
 */
export const WEB_BLUETOOTH_ERROR_MESSAGES: Record<
  WebBLEErrorCode,
  {
    message: string;
    solution: string;
  }
> = {
  [WebBLEErrorCode.NOT_SUPPORTED]: {
    message: "Web Bluetooth is not supported in this browser",
    solution: "Use Chrome, Edge, or Opera browser with Web Bluetooth support",
  },
  [WebBLEErrorCode.INSECURE_CONTEXT]: {
    message: "Web Bluetooth requires a secure context (HTTPS)",
    solution: "Use HTTPS or localhost for development",
  },
  [WebBLEErrorCode.USER_CANCELLED]: {
    message: "Device selection was cancelled",
    solution: "Please select a device from the list to continue",
  },
  [WebBLEErrorCode.DEVICE_NOT_FOUND]: {
    message: "Bluetooth device not found or not available",
    solution: "Ensure device is powered on, in range, and advertising",
  },
  [WebBLEErrorCode.GATT_CONNECTION_FAILED]: {
    message: "Failed to connect to device GATT server",
    solution: "Check device is available and try again",
  },
  [WebBLEErrorCode.SERVICE_NOT_FOUND]: {
    message: "Required service not found on device",
    solution: "Verify device firmware supports the required service",
  },
  [WebBLEErrorCode.CHARACTERISTIC_NOT_FOUND]: {
    message: "Required characteristic not found in service",
    solution: "Check device firmware implements the required characteristics",
  },
  [WebBLEErrorCode.NOTIFICATION_FAILED]: {
    message: "Failed to enable notifications on characteristic",
    solution: "Verify characteristic supports notifications",
  },
  [WebBLEErrorCode.WRITE_FAILED]: {
    message: "Failed to write data to characteristic",
    solution: "Check connection and characteristic write permissions",
  },
  [WebBLEErrorCode.UNEXPECTED_DISCONNECT]: {
    message: "Device disconnected unexpectedly",
    solution: "Check device power and Bluetooth range",
  },
  [WebBLEErrorCode.PERMISSION_DENIED]: {
    message: "Bluetooth permission denied",
    solution: "Allow Bluetooth access in browser settings",
  },
  [WebBLEErrorCode.TIMEOUT]: {
    message: "Operation timed out",
    solution: "Check device availability and try again",
  },
};
